import type { BrainSystem } from './BrainSystem';
import type { SentienceLevel } from '@/types';

const ANIMAL_ASYMPTOTE = 0.5;

export type SentienceListener = (level: SentienceLevel) => void;

export class SentienceGate {
  private level: SentienceLevel;
  private listeners: SentienceListener[] = [];
  private unsubscribe: (() => void) | null = null;

  constructor(private readonly brain: BrainSystem) {
    this.level = this.compute();
    this.unsubscribe = brain.onLevelChange(() => {
      const next = this.compute();
      if (next !== this.level) {
        this.level = next;
        this.emit(next);
      }
    });
  }

  get currentLevel(): SentienceLevel {
    return this.level;
  }

  canRender(minimumLevel: SentienceLevel): boolean {
    return this.level >= minimumLevel;
  }

  onLevelChange(listener: SentienceListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private compute(): SentienceLevel {
    const v = this.brain.currentValue;
    const humanConsumed = this.brain.humanBrainConsumed;
    if (humanConsumed && v >= 0.9) return 4;
    if (v >= ANIMAL_ASYMPTOTE - 0.1) return 3;
    if (v >= 0.3) return 2;
    if (v >= 0.1) return 1;
    return 0;
  }

  private emit(level: SentienceLevel): void {
    for (const listener of this.listeners) {
      listener(level);
    }
  }
}
